import React, { useMemo } from 'react';
import {
    Box, Flex, Text, Badge, Stack, Progress, useColorModeValue,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import { useTranslation } from 'react-i18next';

function pct(count, total) {
    if (!total) return 0;
    return Math.round((count / total) * 100);
}

export default function UpsellsList({
                                              data = [],
                                              title = 'Upsells by Base Product',
                                              maxHeight = '420px',
                                              sortProductsByTotal = true,
                                          }) {
    const { i18n } = useTranslation();
    const l = (en, ro) => (i18n.language === 'ro' ? ro : en);
    const heading = useColorModeValue('secondaryGray.900', 'white');
    const subText = useColorModeValue('gray.600', 'whiteAlpha.800');
    const border = useColorModeValue('gray.200', 'whiteAlpha.200');
    const blockBg = useColorModeValue('gray.50', 'whiteAlpha.50');

    const products = useMemo(() => {
        if (!sortProductsByTotal) return data;
        return [...data].sort((a, b) => {
            const ta = (a.upsells || []).reduce((s, u) => s + (u.order_count || 0), 0);
            const tb = (b.upsells || []).reduce((s, u) => s + (u.order_count || 0), 0);
            return tb - ta;
        });
    }, [data, sortProductsByTotal]);

    return (
        <Card flexDirection="column" w="100%" px="0px">
            <Flex px="24px" py="12px" align="center" justify="space-between">
                <Text color={heading} fontSize="22px" fontWeight="700">{title}</Text>
                <Badge variant="subtle" colorScheme="purple">{products.length} {l('product', 'produs')}{products.length === 1 ? '' : 'e'}</Badge>
            </Flex>

            <Box maxH={maxHeight} overflowY="auto" borderTop="1px solid" borderColor={border} px="16px" py="10px">
                <Stack spacing="14px">
                    {products.map((p) => {
                        const ups = p.upsells || [];
                        const total = ups.reduce((s, u) => s + (u.order_count || 0), 0);

                        return (
                            <Box
                                key={p.product_id}
                                border="1px solid"
                                borderColor={border}
                                bg={blockBg}
                                rounded="md"
                                p="12px"
                            >
                                <Flex align="center" justify="space-between" wrap="wrap" gap="8px" mb="6px" fontSize='14px'>
                                    <Text fontWeight="700">{p.product_name}</Text>
                                    <Badge colorScheme="green" variant="subtle">
                                        {total} {l('order', 'comanda')}{total === 1 ? '' : 'i'}
                                    </Badge>
                                </Flex>

                                {/* No upsells case */}
                                {total === 0 ? (
                                    <Text color={subText} fontSize="sm">{l('No upsells purchased for this product in the selected period.', 'Nu exista upsell-uri cumparate pentru acest produs in perioada selectata.')}</Text>
                                ) : (
                                    <Box pl={{ base: 4, md: 6 }} borderLeft="2px solid" borderColor={border} mt="8px" fontSize='14px'>
                                        <Stack spacing="10px">
                                            {ups
                                                .slice()
                                                .sort((a, b) => b.order_count - a.order_count)
                                                .map((u) => {
                                                    const pcent = pct(u.order_count, total);
                                                    return (
                                                        <Box key={`${p.product_id}-${u.upsell_product_id}`}>
                                                            <Flex align="center" justify="space-between" gap="10px">
                                                                <Text fontWeight="600">{u.upsell_name}</Text>
                                                                <Text color={subText} fontSize="sm">
                                                                    {u.order_count} {l('orders', 'comenzi')} • {pcent}%
                                                                </Text>
                                                            </Flex>
                                                        </Box>
                                                    );
                                                })}
                                        </Stack>
                                    </Box>
                                )}
                            </Box>
                        );
                    })}
                </Stack>
            </Box>
        </Card>
    );
}
